import {AxiosError} from 'axios';

/**
 * The UI was written against Laravel, so every error handler (toasts, form
 * field errors, error pages) expects an AxiosError whose response.data looks
 * like {message, errors: {field: [msg]}}. Supabase/Postgres errors are converted
 * into that shape here so none of those handlers need to change.
 *
 * SQL functions raise structured errors with these conventions:
 *  - validation: `hint = 'validation'`, `detail` = JSON object of field errors
 *  - forbidden:  errcode 42501 (also what RLS violations use)
 *  - not found:  errcode P0002 or `hint = 'not_found'`
 */

export interface BackendErrorPayload {
  message: string;
  errors?: Record<string, string | string[]>;
  status?: number;
}

interface PostgresLikeError {
  message: string;
  code?: string;
  details?: string | null;
  hint?: string | null;
  status?: number;
}

export function backendError({
  message,
  errors,
  status = 422,
}: BackendErrorPayload): AxiosError {
  return new AxiosError(message, String(status), undefined, undefined, {
    status,
    statusText: '',
    headers: {},
    config: {} as any,
    data: {message, errors},
  });
}

export function validationError(
  errors: Record<string, string | string[]>,
): AxiosError {
  const first = Object.values(errors)[0];
  return backendError({
    message: (Array.isArray(first) ? first[0] : first) ?? 'Invalid data',
    errors,
    status: 422,
  });
}

export function notFoundError(message = 'Not found'): AxiosError {
  return backendError({message, status: 404});
}

export function toBackendError(err: PostgresLikeError | null | undefined) {
  if (!err) return backendError({message: 'Unknown error', status: 500});
  if (err instanceof AxiosError) return err;

  if (err.hint === 'validation') {
    let errors: Record<string, string[]> | undefined;
    try {
      errors = err.details ? JSON.parse(err.details) : undefined;
    } catch {
      errors = undefined;
    }
    return backendError({message: err.message, errors, status: 422});
  }

  if (err.code === '42501') {
    // RLS / grant violations get a generic message, functions that raise
    // 42501 themselves (e.g. "Admin users can't be suspended") keep theirs
    const isPostgresMessage = /row-level security|permission denied/i.test(err.message ?? '');
    return backendError({
      message:
        err.message && !isPostgresMessage
          ? err.message
          : "You don't have required permissions for this action.",
      status: 403,
    });
  }

  if (err.code === 'P0002' || err.hint === 'not_found' || err.code === 'PGRST116') {
    return notFoundError(err.message);
  }

  if (err.code === '23505') {
    return backendError({
      message: 'A record with this value already exists.',
      status: 422,
    });
  }

  // auth-js errors carry an HTTP status already
  return backendError({message: err.message, status: err.status ?? 500});
}

/**
 * Await a supabase-js query and return its data, throwing a Laravel-shaped
 * AxiosError on failure.
 */
export async function unwrap<T>(
  promise: PromiseLike<{data: T; error: PostgresLikeError | null}>,
): Promise<T> {
  const {data, error} = await promise;
  if (error) {
    throw toBackendError(error);
  }
  return data;
}
